import { Button, Checkbox, FormControlLabel } from '@material-ui/core';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Privilege, ClassType, RunExportStatus } from '../../../api';
import useHasPrivilege from '../../Util/useHasPrivilege';
import { TSession } from '../Sessions';
import styled from 'styled-components';
import { CircularProgressWithLabel } from '../SessionUploadButton';
import { RunData, TaskType } from './sessionLabelOverviewReducer';
import sessionService from '../../../services/sessions';
import { LabelTask } from '../../../services/sessionModels';
import jobsService, { JobUpdateViewModel, sessionRunStates } from '../../../services/jobs';

type Props = {
  refetch: () => void;
  session?: TSession;
  labelTasks?: LabelTask[];
  runData?: RunData;
};

const SessionLabelExport = (props: Props) => {
  const [exportBall, setExportBall] = useState<boolean>(true);
  const hasDownloadPermission = useHasPrivilege(Privilege.DownloadData);
  const hasExportPermission = useHasPrivilege(Privilege.ExportData);
  const hasAdminPermission = useHasPrivilege(Privilege.Admin);
  const [clickedExport, setClickedExport] = useState<Boolean>(false);

  const totalFrames = useMemo(() => {
    return (
      props.labelTasks
        ?.filter((task) => task.classType !== ClassType.Ball || exportBall)
        .map((task) => task.endFrame - task.startFrame)
        .reduce((p, c) => p + c, 0) || 0
    );
  }, [props, exportBall]);

  const labeledFrames = useMemo(() => {
    return (
      props.runData?.tasks
        ?.filter((task) => task.task_type !== TaskType.Ball || exportBall)
        .map((task) => {
          const lt = props.labelTasks?.find((tlt) => String(tlt.id) === String(task.task_id));
          if (!lt || !task.last_frame_id || task.last_frame_id === -1) return 0;
          return Math.max(0, Math.min(task.last_frame_id, lt.endFrame) - lt.startFrame);
        })
        .reduce((p, c) => p + c, 0) || 0
    );
  }, [props, exportBall]);

  const allPlayerTasksIdentified = useMemo(() => {
    return (
      !!props.labelTasks &&
      props.labelTasks.find((task) => task.classType !== ClassType.Ball && !task.lineupEntry) === undefined
    );
  }, [props]);

  const exportDisabled = useMemo(() => {
    if (clickedExport) {
      return true;
    }

    if (!allPlayerTasksIdentified) {
      return true;
    }
    if (!totalFrames || totalFrames > labeledFrames) {
      return true;
    }
    if (
      !props.session ||
      props.session.lastRun?.exportStatus === RunExportStatus.SavingRun ||
      props.session.lastRun?.exportStatus === RunExportStatus.Exporting
    ) {
      return true;
    }
  }, [allPlayerTasksIdentified, totalFrames, labeledFrames, props, clickedExport]);

  const onExportEpts = useCallback(async (isPythonLa: boolean) => {
    if (props.session?.id)
    {
      setClickedExport(true);
      await sessionService.exportSession(props.session.id, isPythonLa);
      await props.refetch()
    }
  }, [props]);

  const onToggleExportBall = useCallback(() => {
    setExportBall((prev) => !prev);
  }, []);

  return hasDownloadPermission || hasExportPermission ? (
    <ExportContainer>
      {exportDisabled && <Progress totalFrames={totalFrames} labeledFrames={labeledFrames} session={props.session} />}
      <FormControlLabel
        control={<Checkbox checked={exportBall} onChange={onToggleExportBall} />}
        label="Include labeled ball"
      />
      <Button disabled={exportDisabled} variant="contained" color="primary" onClick={() => onExportEpts(false)}>
        Export
      </Button>
      
      {hasAdminPermission && (
      <Button
        disabled={exportDisabled}
        variant="contained"
        color="primary"
        onClick={() => onExportEpts(true)}
      >
        Export - Python
      </Button>
      )}
    </ExportContainer>
  ) : (
    <div />
  );
};

type ProgressProps = {
  labeledFrames: number;
  totalFrames: number;
  session?: TSession | null;
};

const Progress = ({ session, totalFrames, labeledFrames }: ProgressProps) => {
  const [percentage, setPercentage] = useState<number>(0);
  const [header, setHeader] = useState('Labelling');
  const onJobUpdate: (result: JobUpdateViewModel) => void = useCallback(i => {
    sessionRunStates.setRun(i.runId, i);
  }, []);

  useEffect(() => {

      const lastRunId = session?.lastRun?.id || "-1";

      sessionRunStates.addHandler(lastRunId, r => {

        if (r.exportJob !== null) {
          setHeader('Exporting')
          setPercentage((r?.exportJob?.progress || 0 ) * 100);
        }
        else if (r.saveJob !== null) {
          setHeader('Saving');
          setPercentage((r?.saveJob?.progress || 0) * 100);
        }
      });

      jobsService.subscribeToJob([lastRunId])

      if (session?.lastRun?.exportStatus !== RunExportStatus.SavingRun && 
        session?.lastRun?.exportStatus !== RunExportStatus.Exporting
      ) {
        setHeader('Labelling');
        setPercentage(Math.floor((100 * labeledFrames) / totalFrames));
      }

      return () => {
        sessionRunStates.removeHandler(lastRunId);
      }
  }, [session, labeledFrames, totalFrames, onJobUpdate, setPercentage]);

  return (
    <ProgressContainer>
      <Header>{header}</Header>
      <CircularProgressWithLabel size={60} value={percentage} />
      {session?.lastRun?.exportStatus === RunExportStatus.Failed && <div>Last export failed</div>}
    </ProgressContainer>
  );
};

const ExportContainer = styled.div`
  border-top: 1px solid darkgray;
  padding-top: 16px;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.div`
  font-size: 20px;
  font-weight: 500;
`;

export default SessionLabelExport;
